import useLanguage from '@/composables/useLanguage'
import { IMappedRemedy, ISketch, TBannerVariant } from '@lifk/types'
import { pointWithinPolygon } from '@lifk/utils'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useBannerStore = defineStore('banner', () => {
  const warningI18n = useLanguage('WarningBanner')

  const warningText = ref<string | undefined>(undefined)
  const warningVariant = ref<TBannerVariant>('warning')
  const errorText = ref<string | undefined>(undefined)
  const successText = ref<string | undefined>(undefined)
  const newMappedRemedy = ref<IMappedRemedy | undefined>(undefined)
  const oldMappedRemedy = ref<IMappedRemedy | undefined>(undefined)
  const withinPolygonChanged = ref<boolean>(false)

  type TFlashVariant = 'success' | 'warn' | 'error' | 'default'

  const flashQueue = ref<{ type: TFlashVariant; message: string }[]>([])

  const setUpdatedMappedRemedy = (
    selectedSite: ISketch,
    newMR: Omit<IMappedRemedy, 'isWithinPolygon'>,
    oldMR?: IMappedRemedy
  ) => {
    clearOldBanners()

    if (newMR) {
      const fullNewMR: IMappedRemedy = {
        ...newMR,
        isWithinPolygon: pointWithinPolygon(
          newMR.location.coordinates,
          selectedSite.coordinates
        )
      }

      withinPolygonChanged.value =
        !oldMR || fullNewMR.isWithinPolygon !== oldMR.isWithinPolygon
      if (withinPolygonChanged.value) {
        if (
          fullNewMR.remedy.exceedsSiteFootprint &&
          fullNewMR.isWithinPolygon
        ) {
          warningText.value = warningI18n.value.totalFootprintBanner
        } else if (
          fullNewMR.remedy.sumRemediesExceedsSiteFootprint &&
          fullNewMR.isWithinPolygon
        ) {
          warningText.value = warningI18n.value.sumFootprintsBanner
        }
      }
      newMappedRemedy.value = fullNewMR
      oldMappedRemedy.value = oldMR
    }
  }

  const flashWarning = (warningMessage: string, useWarningStyle = true) => {
    clearOldBanners()
    warningText.value = warningMessage
    // default = charcoal background, neutral tone
    // warning = orange with warning icon
    warningVariant.value = useWarningStyle ? 'warning' : 'default'
  }

  const flashError = (errorMessage: string) => {
    clearOldBanners()
    errorText.value = errorMessage
  }

  const flashSuccess = (successMessage: string) => {
    clearOldBanners()
    successText.value = successMessage
  }

  const addToFlashQueue = (banner: {
    type: TFlashVariant
    message: string
  }) => {
    flashQueue.value.push(banner)
  }

  const flashNextInQueue = async () => {
    // If nothing in the queue, or a banner is already showing, do nothing
    if (
      !flashQueue.value.length ||
      warningText.value ||
      errorText.value ||
      successText.value
    )
      return

    // Take the first banner out of the queue
    const first = flashQueue.value[0]
    if (!first) return

    if (first.type === 'success') {
      flashSuccess(first.message)
    } else if (first.type === 'warn' || first.type === 'default') {
      flashWarning(first.message, first.type === 'warn')
    } else if (first.type === 'error') {
      flashError(first.message)
    }

    // Wait for state to update (ie wait for show.value to flip to true)
    //   before updating the queue (which triggers the watcher again)
    setTimeout(() => {
      flashQueue.value.shift()
    }, 10)
  }

  const clearOldBanners = () => {
    warningText.value = undefined
    warningVariant.value = 'warning'
    errorText.value = undefined
    successText.value = undefined
    newMappedRemedy.value = undefined
    oldMappedRemedy.value = undefined
    withinPolygonChanged.value = false
  }

  return {
    warningText,
    warningVariant,
    errorText,
    successText,
    newMappedRemedy,
    oldMappedRemedy,
    withinPolygonChanged,
    flashQueue,
    setUpdatedMappedRemedy,
    clearOldBanners,
    addToFlashQueue,
    flashNextInQueue
  }
})
